import { useEffect, useRef } from 'react';
import { useAudioStore } from './useAudioStore';
import { useAudioPlayer } from './useAudioPlayer';

/**
 * Hook koji pauzira AzuraCast audio dok je flag `active=true` (npr. dok je
 * korisnik na LiveVideoScreen-u i sluša audio iz live video stream-a).
 *
 * Ponašanje:
 *  - Pri prelasku active=false → true: snimi `isPlaying` snapshot, ako je
 *    bio true pozove `pause()`.
 *  - Pri unmount-u (ili active=true → false): ako je bio playing na mount-u,
 *    pozove `togglePlayPause()` koji rebuilds-uje stream konekciju (live
 *    radio nema seek).
 *
 * KRITIČNO — latest-ref pattern:
 *   `useAudioPlayer` vraća `pause` / `togglePlayPause` kao `useCallback`
 *   rezultate. `togglePlayPause` ima `isPlaying` u deps, pa se rekreira
 *   na svaki play/pause toggle. Da useEffect dependency [active, pause,
 *   togglePlayPause] NE bi trigerovao cleanup pri internim toggle-ima
 *   (što bi pogasilo wasPlayingRef i pokvarilo resume logiku), držimo
 *   funkcije u ref-ovima i ažuriramo ih izvan useEffect-a. Effect
 *   trigeruje SAMO kad `active` se mijenja.
 */
export function useAudioPause(active: boolean): void {
  const { pause, togglePlayPause } = useAudioPlayer();

  const pauseRef = useRef(pause);
  const togglePlayPauseRef = useRef(togglePlayPause);
  pauseRef.current = pause;
  togglePlayPauseRef.current = togglePlayPause;

  const wasPlayingOnMountRef = useRef(false);

  useEffect(() => {
    if (!active) return;

    const wasPlaying = useAudioStore.getState().isPlaying;
    wasPlayingOnMountRef.current = wasPlaying;
    if (wasPlaying) {
      pauseRef.current();
    }

    return () => {
      if (wasPlayingOnMountRef.current) {
        wasPlayingOnMountRef.current = false;
        togglePlayPauseRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
